import { getBrowserLocales, locales } from '@cyren/common-lib';
import classNames from 'classnames';
import { capitalize, isEmpty, lowerCase, trim } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { PiMagnifyingGlass } from 'react-icons/pi';
import { useLocation } from 'react-router';
import { H3 } from '../common/typo/H';
import Modal from '../components/Modal';
import { useDebInputState } from '../hooks/use-deb-input-state';
import useAppState from './use-app-state';
import { useStaticLocales } from '../admin/hooks/locales/use-static-locale';
import { usePaSurveyParams } from '../report/use-pa-survey-params';
import { customInstance } from '../v2/api/custom-instance';
import {
  buildAllowedLocaleKeysForV2,
  filterLocaleOptionsByAllowedKeys,
} from './modal-locale-utils';
import { usePatientLocale } from '../v2/hooks/use-patient-locale';

// UI text용 언어 (admin 페이지)
const uiTextLocales = locales.filter((l) => ['english', 'korean', 'japanese'].includes(l.key));

function ModalLocale({ uiTextOnly = false }: { uiTextOnly?: boolean }) {
  const { sltStr } = useStaticLocales();
  const [
    {
      appState: { openLocale },
      appStateCookie: { userLocaleKey: adminUserLocaleKey },
    },
    { setLocale: adminSetLocale, closeLocaleModal },
  ] = useAppState();

  const patientLocale = usePatientLocale();

  const location = useLocation();
  const { orgId } = usePaSurveyParams();
  const [allowedLocaleKeysForV2, setAllowedLocaleKeysForV2] = useState<string[] | null>(null);
  const [isLoadingAllowedLocaleKeys, setIsLoadingAllowedLocaleKeys] = useState(false);

  const isV2PatientRoute = location.pathname.startsWith('/v2/s');
  const browserLangKeys = getBrowserLocales();
  const fetching = isLoadingAllowedLocaleKeys;

  const setLocale = isV2PatientRoute
    ? (locale: string) => { patientLocale.setLocale(locale); closeLocaleModal(); }
    : adminSetLocale;
  const userLocaleKey = isV2PatientRoute ? patientLocale.userLocaleKey : adminUserLocaleKey;

  const { input, ref, setInput, debInput } = useDebInputState({ noInitFocus: true });
  const hasUserInput = !!userLocaleKey;

  useEffect(() => {
    if (!isV2PatientRoute) {
      setAllowedLocaleKeysForV2(null);
      setIsLoadingAllowedLocaleKeys(false);
      return undefined;
    }

    const organizationId = Number(orgId);
    if (!organizationId || Number.isNaN(organizationId)) {
      setAllowedLocaleKeysForV2(['english']);
      setIsLoadingAllowedLocaleKeys(false);
      return undefined;
    }

    let isActive = true;
    setIsLoadingAllowedLocaleKeys(true);

    const fetchAllowedLocales = async () => {
      try {
        const [defaultLanguageRes, supportLanguagesRes] = await Promise.all([
          customInstance<{ value?: string }>({
            method: 'GET',
            url: `/config/public/default-language/${organizationId}`,
          }),
          customInstance<{ value?: string[] }>({
            method: 'GET',
            url: `/config/public/translation-support-languages/${organizationId}`,
          }),
        ]);

        if (!isActive) return;

        setAllowedLocaleKeysForV2(
          buildAllowedLocaleKeysForV2({
            defaultLanguage: defaultLanguageRes?.value,
            translationSupportLanguages: supportLanguagesRes?.value || [],
          }),
        );
      } catch (error) {
        // Keep locale modal usable with safe fallback when config call fails.
        if (isActive) {
          setAllowedLocaleKeysForV2(['english']);
        }
      } finally {
        if (isActive) {
          setIsLoadingAllowedLocaleKeys(false);
        }
      }
    };

    fetchAllowedLocales();

    return () => {
      isActive = false;
    };
  }, [isV2PatientRoute, orgId]);

  // uiTextOnly가 true면 3개 언어만, false면 전체 언어
  const baseLocaleList = uiTextOnly ? uiTextLocales : locales;
  const localeList = useMemo(() => {
    if (!isV2PatientRoute) {
      return baseLocaleList;
    }

    return filterLocaleOptionsByAllowedKeys(baseLocaleList, allowedLocaleKeysForV2 || ['english']);
  }, [allowedLocaleKeysForV2, baseLocaleList, isV2PatientRoute]);

  const reorderedList = [...localeList].sort((a, b) => {
    // @ts-ignore
    const found1 = browserLangKeys.includes(a.key);
    // @ts-ignore
    const found2 = browserLangKeys.includes(b.key);
    if (found1 && found2) {
      return 0;
    }
    if (found1 && !found2) {
      return -1;
    }

    return 1;
  });

  const localesFiltered = !isEmpty(trim(debInput))
    ? reorderedList?.filter((l) => {
        return (
          lowerCase(l.label).includes(lowerCase(debInput)) ||
          lowerCase(l.key).includes(lowerCase(debInput))
        );
      })
    : reorderedList;

  return (
    <Modal
      open={openLocale}
      className='ModalSxCollector h-full rounded-t-xl mt-6 px-0 overflow-hidden py-0 pb-4 !max-w-[550px] lg:!w-[550px]'
      onClickBackdrop={() => {
        closeLocaleModal();
      }}
    >
      <div className='flex-1 flex-col gap-1 overflow-hidden'>
        <div className='flex-col flex-none gap-2 overflow-hidden pb-4'>
          <div className='flex-none flex-center'>
            {/* top modal handle */}
            <div className='bg-black bg-opacity-10 rounded-full w-10 h-2 mt-3'></div>
          </div>
          <div className='flex-none flex-center-y gap-2 px-5'>
            <H3 className='flex-1 text-center'>{sltStr({ key: 'modal-locale-title' })}</H3>
            <div className='w-0 overflow-visible'>
              <button
                className={classNames('p-2 inline', !hasUserInput && 'hidden')}
                onClick={async () => {
                  if (userLocaleKey) setLocale(userLocaleKey);
                }}
              >
                <MdClose size={20} className='opacity-60 -top-4 relative right-7' />
              </button>
            </div>
          </div>
        </div>

        <div className='flex-col flex-1 overflow-hidden'>
          <div className='form-control border-y flex-none'>
            <div className='flex'>
              <span className={classNames('flex-none flex-center p-2 px-3 bg-transparent')}>
                <div
                  className={classNames(
                    'block relative w-0 overflow-visible h-0',
                    !fetching && 'opacity-0',
                  )}
                >
                  <div className='absolute -top-2'>
                    <span className='loading loading-spinner loading-xs opacity-50'></span>
                  </div>
                </div>
                <PiMagnifyingGlass
                  size={18}
                  className={classNames('opacity-60', fetching && 'opacity-0')}
                />
              </span>
              <input
                type='text'
                ref={ref}
                value={input}
                placeholder={sltStr({ key: 'modal-locale-search' })}
                className='flex-1 border-none py-3 pr-2 outline-none text-sm'
                onChange={(e) => {
                  setInput(e.target.value);
                }}
              />
            </div>
          </div>

          <div className='flex-col flex-1 overflow-auto max-h-[40em]'>
            {localesFiltered.map((locale) => {
              // @ts-ignore
              const preferred = browserLangKeys.includes(locale.key);

              return (
                <div
                  className={classNames(
                    'px-10 py-3 border-b cursor-pointer',
                    'hover:bg-gray-200',
                    userLocaleKey === locale.key ? 'bg-primary bg-opacity-20' : 'bg-base-100',
                    preferred && 'font-bold',
                  )}
                  key={locale.key}
                  onClick={async () => {
                    setLocale(locale.key);
                  }}
                >
                  {capitalize(locale.label)}{' '}
                  {locale.label !== locale.key && (
                    <span className='opacity-50 text-xs'>({capitalize(locale.key)})</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default ModalLocale;
